import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  tableCellClasses,
} from "@mui/material";
import ProgressBar from "../../ProgressBars/ProgressBar";
import ColoredBox from "../../Boxes/TopProductBox/ColoredBox";
import { theme } from "../../../Theme/index";
import FacebookCircularProgress from "../../shared/shared/Loading";
import { getUrl } from "src/Utils/Common";
import { useSelector } from "react-redux";

function TopProd({ sx }) {
  const selectedFilter = useSelector((state) => state.filter.selectedFilter); // ✅ Get Redux filter

  const getApiFilters = () => {
    if (!selectedFilter || selectedFilter.length === 0) return {};

    return selectedFilter.reduce((acc, item) => {
      const { category, name } = item;

      if (!acc[category]) {
        acc[category] = []; // Initialize category array if not exists
      }

      acc[category].push(name); // Push the name value
      return acc;
    }, {});
  };

  // Redux: Get project ID
  const projectId = useSelector((state) => state.userProject.projectId);
  const currentChartType = useSelector((state) => state.chart.currentChartType);

  // Local State
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Styled Table Components
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      padding: "5px",
      color: theme.palette.primary.dark,
      fontSize: theme.typography.fontSize.xxsmall,
      textAlign: "center",
      justifyContent: "center",
    },
    [`&.${tableCellClasses.body}`]: {
      textTransform: "capitalize",
      color: theme.palette.primary.dark,
      fontSize: 10,
      textAlign: "center",
      padding: "3px",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    height: 15,
  }));

  // Fetch API Data
  useEffect(() => {
    async function fetchTopProducts() {
      if (!projectId) {
        console.warn("Project ID is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Extract hierarchy and hierarchy_value dynamically
      const [hierarchy, hierarchy_value] = currentChartType?.split(",") || [
        "brand",
        "AFIA",
      ];
      try {
        const response = await fetch(
          `${getUrl()}/market_overview/top_products`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
            },
            body: JSON.stringify({
              project_id: projectId,
              hierarchy: hierarchy,

              area: null,
              filters: getApiFilters(),
              top_5: true,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();

        if (!result || Object.keys(result).length === 0) {
          throw new Error("Invalid API response structure.");
        }

        // Transform API response into a usable format
        const formattedData = result["Name"].map((name, index) => ({
          name,
          volumeSalesY22: result["Volume Sales 2022"]?.[index] || 0,
          volumeSalesY23: result["Volume Sales 2023"]?.[index] || 0,
          valueSalesY22: result["Value Sales 2022"]?.[index] || 0,
          valueSalesY23: result["Value Sales 2023"]?.[index] || 0,
          dya: result["DYA"]?.[index] || 0,
          iya: result["IYA"]?.[index] || 0,
          growthIdx: result["Growth IDX"]?.[index] || 0,
        }));

        setData(formattedData);
      } catch (error) {
        console.error("Error fetching top products:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }

    fetchTopProducts();
  }, [projectId, currentChartType, selectedFilter]);

  return (
    <>
      <Typography fontWeight={theme.typography.fontWeightBold} p={1}>
        Top Products
      </Typography>

      {loading ? (
        <FacebookCircularProgress />
      ) : error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : (
        <TableContainer sx={{ height: 200 }}>
          <Table aria-label="customized table">
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: "#ffffff",
                zIndex: 10,
              }}
            >
              <TableRow>
                <StyledTableCell>#</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Volume Sales Y22</StyledTableCell>
                <StyledTableCell>Volume Sales Y23</StyledTableCell>
                <StyledTableCell>Value Sales Y22</StyledTableCell>
                <StyledTableCell>Value Sales Y23</StyledTableCell>
                <StyledTableCell>DYA</StyledTableCell>
                <StyledTableCell>IYA</StyledTableCell>
                <StyledTableCell>Growth IDX</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item, idx) => (
                <StyledTableRow key={idx}>
                  <StyledTableCell>{idx + 1}</StyledTableCell>
                  <StyledTableCell>{item.name}</StyledTableCell>
                  <StyledTableCell>
                    {item.volumeSalesY22.toLocaleString()}
                  </StyledTableCell>
                  <StyledTableCell>
                    {item.volumeSalesY23.toLocaleString()}
                  </StyledTableCell>
                  <StyledTableCell>
                    {`${item.valueSalesY22.toLocaleString()}$`}
                  </StyledTableCell>
                  <StyledTableCell>
                    {`${item.valueSalesY23.toLocaleString()}$`}
                  </StyledTableCell>
                  <StyledTableCell>
                    <ColoredBox
                      number={item.dya}
                      Color={
                        item.dya > 0
                          ? "#0BB783"
                          : item.dya === 0
                          ? "#0095FF"
                          : "#B22234"
                      }
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <ColoredBox
                      number={item.iya}
                      Color={
                        item.iya > 1
                          ? "#0BB783"
                          : item.iya === 1
                          ? "#0095FF"
                          : "#B22234"
                      }
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <ColoredBox
                      number={item.growthIdx}
                      Color={
                        item.growthIdx > 1
                          ? "#0BB783"
                          : item.growthIdx === 1
                          ? "#0095FF"
                          : "#B22234"
                      }
                    />
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

export default TopProd;
